import { DataViewOption, ReportBlockProps } from '@va/types/report-block';
import { FlagsmithFeatureFlags } from '@va/util/helpers';
import { useIsFeatureEnabled } from '@va/util/hooks';
import { IntersectionObserverProvider } from '@va/util/misc';
import { Dispatch, PropsWithChildren, SetStateAction, createContext, useContext, useMemo, useState } from 'react';
import {
  ColumnSizingProvider,
  ColumnVisibilityProvider,
  useColumnSizingContext,
  useColumnVisibilityContext,
} from '../data-table';
import { getReportBlockPaginationId } from './report-block.helpers';

// TODO Move and fix duplicate
export enum DateComparisonPeriod {
  PREVIOUS = 'previous',
  YOY = 'yoy',
}

export type ReportBlockCtx = ReportBlockProps & {
  showPrevious: boolean;
  setShowPrevious: Dispatch<SetStateAction<boolean>>;
  activeComparisonOption: DateComparisonPeriod;
  setActiveComparisonOption: Dispatch<SetStateAction<DateComparisonPeriod>>;
  paginationId: string;
};

const ReportBlockCtx = createContext<ReportBlockCtx>({} as ReportBlockCtx);

export const ReportBlockProvider = ({ children, ...props }: PropsWithChildren<ReportBlockProps>) => {
  const [showPrevious, setShowPrevious] = useState<boolean>(props.showPrevious ?? false);
  const [activeComparisonOption, setActiveComparisonOption] = useState(DateComparisonPeriod.PREVIOUS);
  const isColumnResizingEnabled = useIsFeatureEnabled(FlagsmithFeatureFlags.ColumnResizing);
  const { isProvided: isColumnVisibilityAlreadyProvided } = useColumnVisibilityContext();
  const { isProvided: isColumnSizingAlreadyProvided } = useColumnSizingContext();

  const paginationId = useMemo(() => getReportBlockPaginationId(props.id), [props.id]);

  const contextValue = {
    ...props,
    showPrevious,
    setShowPrevious,
    activeComparisonOption,
    setActiveComparisonOption,
    paginationId,
  };

  const selectedView = props.visualization?.selectedView ?? DataViewOption.table;

  let content = <IntersectionObserverProvider>{children}</IntersectionObserverProvider>;

  if (!isColumnVisibilityAlreadyProvided) {
    content = (
      <ColumnVisibilityProvider reportBlockId={props.id} selectedView={selectedView}>
        {content}
      </ColumnVisibilityProvider>
    );
  }

  if (isColumnResizingEnabled && !isColumnSizingAlreadyProvided) {
    content = (
      <ColumnSizingProvider reportBlockId={props.id} selectedView={selectedView}>
        {content}
      </ColumnSizingProvider>
    );
  }

  return <ReportBlockCtx.Provider value={contextValue}>{content}</ReportBlockCtx.Provider>;
};

export const useReportBlockCtx = () => useContext(ReportBlockCtx);
